<template>
  <div class="kpi-view-trends">
    <HorizontalLoading v-if="KpiViewTrends.isLoading" />
    <div class="trends">
      <div class="chart-header">
        <div class="title">
          <span> {{ normalizeString(selectedKpi) }} </span>
          <div class="sub-title">
            {{ $t('Category Score Trends') }}
          </div>
        </div>
        <div class="chart-header-buttons">
          <div class="trend-types">
            <el-button v-for="type in trendTypes"
                       :key="type.value"
                       round
                       size="mini"
                       :type="type.value === selectedTrendType ? 'primary' : 'default'"
                       @click="selectTrendType(type.value)">
              {{ type.label }}
            </el-button>
          </div>
          <ChartButtons @chartDownload="chartDownload" />
        </div>
      </div>

      <div class="trend-chart" />
    </div>
  </div>
</template>

<script>
import { createCrudState } from 'vuex-module-generator';
import Highcharts from 'highcharts';
import { mapGetters } from 'vuex';
import { chain } from 'lodash';
import i18n from '@/i18n';
import ChartButtons from '@/components/ChartButtons';
import chartButtonAction from '@/helpers/mixins/chartButtonAction';
import { fetchKpiViewTrends } from '../api';
import ExecutiveViewKpiBus from '../bus';

export default {
  name: 'KpiViewTrends',
  components: { ChartButtons },
  mixins: [chartButtonAction],
  props: {
    periods: {
      type: Object,
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    selectedKpi: {
      type: String,
      default: ''
    },
    auditorAndStoreFilters: {
      type: Object,
      required: true
    },
    collectedTags: {
      type: Array,
      default() {
        return [];
      }
    },
    teamGroupFilter: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ExecutiveViewKpiBus,
      selectedTrendType: 'monthly_trend',
      KpiViewTrends: createCrudState().active
    };
  },
  computed: {
    ...mapGetters(['scoreTemplates']),
    filteredCollectedTagsIds() {
      return this.collectedTags.reduce((acc, item) => {
        acc.push(...item.tags.map(tag => tag.id));

        return acc;
      }, []);
    },
    trendTypes() {
      const list = [
        {
          label: i18n.t('Monthly'),
          value: 'monthly_trend'
        },
        {
          label: i18n.t('Weekly'),
          value: 'weekly_trend'
        },
        {
          label: i18n.t('Daily'),
          value: 'daily_trend'
        }
      ];

      if (this.periods.selected_type === 'month') {
        return list.slice(1);
      }

      return list;
    },
    series() {
      const checkVisibility = (item) => {
        const foundLegend = ExecutiveViewKpiBus.legends.find(l => l.name === item);

        return foundLegend ? foundLegend.visible : true;
      };

      return chain(this.KpiViewTrends)
        .result('data', [])
        .find(item => item.key === this.selectedKpi)
        .result('value', [])
        .find(item => item.key === this.selectedTrendType)
        .result('value', [])
        .map(item => ({
          visible: checkVisibility(item.key),
          name: item.key,
          data: item.value ? item.value.filter(i => i) : []
        }))
        .sortBy('name')
        .value();
    }
  },
  watch: {
    collectedTags() {
      this.fetchData();
    },
    periods() {
      this.fetchData();
    },
    auditorAndStoreFilters() {
      this.fetchData();
    },
    selectedKpi() {
      this.renderChart();
    },
    teamGroupFilter() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();

    ExecutiveViewKpiBus.$on('toggleLegend', () => {
      this.renderChart();
    });
  },
  methods: {
    selectTrendType(type) {
      this.selectedTrendType = type;
      this.renderChart();
    },
    async fetchData() {
      if (this.periods.selected_type === 'year') {
        this.selectedTrendType = 'monthly_trend';
      } else if (this.periods.selected_type === 'quarter') {
        this.selectedTrendType = 'weekly_trend';
      } else {
        this.selectedTrendType = 'daily_trend';
      }

      const {
        projectId, periods, auditorAndStoreFilters, teamGroupFilter
      } = this;

      this.KpiViewTrends.isLoading = true;

      try {
        const { data: { data } } = await fetchKpiViewTrends(projectId, {
          filter: {
            stores: {
              tags: this.filteredCollectedTagsIds
            },
            project: projectId,
            auditor_ids: auditorAndStoreFilters.auditor_ids,
            store_ids: auditorAndStoreFilters.store_ids,
            team_group_id: teamGroupFilter.id,
            start_date_with_time: this.convertToServerTimeZone(periods.current.start_time_without_utc),
            end_date_with_time: this.convertToServerTimeZone(periods.current.end_time_without_utc)
          }
        });

        this.KpiViewTrends.data = data;

        await this.$nextTick();

        this.renderChart();

        this.KpiViewTrends.isLoaded = true;
      } catch (e) {
        this.KpiViewTrends.hasError = true;
      }

      this.KpiViewTrends.isLoading = false;
    },
    renderChart() {
      const getOptions = () => ({
        exporting: {
          enabled: true,
          chartOptions: {
            legend: {
              align: 'left',
              layout: 'horizontal',
              enabled: true,
              floating: false,
              verticalAlign: 'bottom',
              margin: 30,
              itemMarginBottom: 10,
              itemDistance: 10
            }
          },
          buttons: {
            contextButton: {
              enabled: false,
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadCSV', 'downloadSVG']
            }
          }
        },
        xAxis: { type: 'datetime', labels: { align: 'right' } },
        yAxis: { labels: { format: '{value:.0f}' }, title: { text: '' } },
        title: { text: '', margin: 0 },
        legend: {
          enabled: false
        },
        tooltip: {
          pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:.0f}</b><br/>'
        },
        plotOptions: {
          series: {
            connectNulls: true,
            marker: {
              symbol: 'bullet'
            }
          }
        },
        credits: { enabled: false },
        series: this.series
      });

      if (this.chart) {
        this.chart.update(getOptions(), true, true, true);
      } else {
        this.chart = Highcharts.chart(this.$el.querySelector('.trend-chart'), getOptions());
      }
    }
  }
};
</script>

<style lang="scss">
.kpi-view-trends {
  position: relative;
  padding: 20px;
  margin-bottom: 20px;
  background-color: white;

  .trends {
    .chart-header {
      .title {
        margin-bottom: 20px;
        font-size: 18px;

        span {
          font-weight: 500;
          color: $darkSkyBlue;
        }

        @include breakpoint(lg-max) {
          display: flex;
          margin-bottom: 0;

          span {
            margin-right: 14px;
            font-weight: 600;
          }
        }

        @include breakpoint(md-s-max) {
          display: block;
          margin-bottom: 10px;

          span {
            font-size: 16px;
          }
        }

        .sub-title {
          font-size: 14px;

          @include breakpoint(lg-max) {
            display: flex;
            align-items: center;
            font-size: 18px;
            font-weight: 400;
            color: #555;

            .mobile-arrow {
              i {
                color: $vispera-blue;
              }
            }
          }

          @include breakpoint(md-s-max) {
            font-size: 22px;
          }
        }
      }

      .chart-header-buttons {
        display: flex;
        justify-content: space-between;
      }

      @include breakpoint(lg-max) {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      @include breakpoint(md-s-max) {
        display: block;
      }
    }
  }

  .chart {
    box-sizing: border-box;
    width: 100%;
    background-color: white;
  }
}
</style>
